;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/:slug*\",\"regex\":\"^/(.+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/article/:code\",\"regex\":\"^/article/([^/]+)$\",\"paramNames\":[\"code\"],\"hasOptionalPrefix\":false},{\"path\":\"/articles/:slug*?\",\"regex\":\"^/articles(?:/(.*))?$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/brands/:slug\",\"regex\":\"^/brands/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/catalog/:slug*?\",\"regex\":\"^/catalog(?:/(.*))?$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/news/:code\",\"regex\":\"^/news/([^/]+)$\",\"paramNames\":[\"code\"],\"hasOptionalPrefix\":false},{\"path\":\"/product/:code\",\"regex\":\"^/product/([^/]+)$\",\"paramNames\":[\"code\"],\"hasOptionalPrefix\":false},{\"path\":\"/oauth/:provider/callback\",\"regex\":\"^/oauth/([^/]+)/callback$\",\"paramNames\":[\"provider\"],\"hasOptionalPrefix\":false},{\"path\":\"/product/find/:externalId\",\"regex\":\"^/product/find/([^/]+)$\",\"paramNames\":[\"externalId\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/brands\"},{\"path\":\"/bug-report\"},{\"path\":\"/cart\"},{\"path\":\"/certificates\"},{\"path\":\"/contacts\"},{\"path\":\"/favourite\"},{\"path\":\"/login\"},{\"path\":\"/news\"},{\"path\":\"/order\"},{\"path\":\"/order/success\"},{\"path\":\"/profile\"},{\"path\":\"/profile/bonus\"},{\"path\":\"/profile/favourite\"},{\"path\":\"/profile/orders\"},{\"path\":\"/profile/personal\"},{\"path\":\"/profile/reserve\"},{\"path\":\"/profile/service-records\"},{\"path\":\"/sale\"},{\"path\":\"/service/func-testing\"},{\"path\":\"/service/shoe-testing\"},{\"path\":\"/video\"},{\"path\":\"/logout\"},{\"path\":\"/smart-captcha\"},{\"path\":\"/t\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.2.6";globalThis["_sentryRewritesTunnelPath"] = "/monitoring";import {isExpectedError} from '@/src/shared/utils/expected-error'
// This file configures the initialization of Sentry on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from '@sentry/nextjs'
import {settings} from '@/src/config/settings'
import {initPlatform} from '@/src/shared/utils/platform'

const isMobile = process.env.NEXT_PUBLIC_PROJECT === 'mobile'

const tracesWithErrors = new Set<string>()

Sentry.init({
  dsn: 'https://bf49d3f8112a9638444536da2e157881@sentry.appgate.ru/8',
  environment: process.env.NEXT_PUBLIC_SENTRY_ENV,

  // Define how likely traces are sampled. Adjust this value in production, or use tracesSampler for greater control.
  tracesSampleRate: 0.1,
  // Enable logs to be sent to Sentry
  enableLogs: true,

  _experiments: {
    enableLogs: true,
  },
  // Define how likely Replay events are sampled.
  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  // replaysSessionSampleRate: 0.1,
  //
  // // Define how likely Replay events are sampled when an error occurs.
  // replaysOnErrorSampleRate: 1.0,

  // Enable sending user PII (Personally Identifiable Information)
  // https://docs.sentry.io/platforms/javascript/guides/nextjs/configuration/options/#sendDefaultPii
  sendDefaultPii: true,
  tracesSampler: samplingContext => {
    if (
      samplingContext.normalizedRequest?.url &&
      samplingContext.normalizedRequest.url.search(/(\/_next\/static|\/_next\/image)/) >= 0
    ) {
      return 0.0 // never send transactions with name GET /api/health
    }

    return samplingContext.attributes?.custom == true &&
      settings.NEXT_PUBLIC_APP_ENV === 'production'
      ? 1
      : 0.1
  },

  transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
  transportOptions: {},

  beforeSend(event, hint) {
    const error = hint.originalException ?? hint.syntheticException
    if (isExpectedError(error)) {
      return null
    }

    if (event.type === undefined && event.contexts?.trace?.trace_id) {
      tracesWithErrors.add(event.contexts.trace.trace_id)

      // cleanup через время
      setTimeout(
        () => {
          tracesWithErrors.delete(event.contexts!.trace!.trace_id)
        },
        5 * 60 * 1000,
      )
    }

    return event
  },

  beforeSendTransaction(event) {
    const traceId = event.contexts?.trace?.trace_id

    if (traceId && tracesWithErrors.has(traceId)) {
      return event
    }

    // if (['development', 'local'].includes(settings.NEXT_PUBLIC_APP_ENV)) {
    //   return event
    // }

    return null
  },

  beforeSendLog(log) {
    const traceId = log.attributes?.trace_id as string | undefined

    if (traceId) {
      if (
        tracesWithErrors.has(traceId)
        // || ['development', 'local'].includes(settings.NEXT_PUBLIC_APP_ENV)
      ) {
        return log
      }
    } else {
      if (
        settings.NEXT_PUBLIC_APP_ENV === 'production' &&
        ['error', 'warn', 'fatal'].includes(log.level)
        // ||        ['development', 'local'].includes(settings.NEXT_PUBLIC_APP_ENV)
      ) {
        return log
      }
    }

    return null
  },
})

Sentry.getCurrentScope().setLevel('info')

if (isMobile) {
  initPlatform().then(() =>
    import('@runlab/mobile-utils/lib/updates/utils').then(({waitReadyUpdates, waitVersion}) => {
      const checkUpdates = async (initialBundleId: string) => {
        try {
          const {settings} = await import('@/src/config/settings')
          const {getInjection} = await import('@runlab/di')
          const {
            ApplyUpdateStrategy,
            CapacitorAppInfoProvider,
            CapacitorBundleManager,
            HttpVersionFetcher,
            LiveUpdateService,
          } = await import('@runlab/mobile-utils/lib/updates/updates')

          const eventBus = getInjection('IEventBus')
          const appInfoProvider = new CapacitorAppInfoProvider(initialBundleId)
          const bundleManager = new CapacitorBundleManager(initialBundleId)
          const versionFetcher = new HttpVersionFetcher(
            settings.NEXT_PUBLIC_FRONTEND_URL,
            settings.NEXT_PUBLIC_MOBILE_TOKEN!,
          )
          const updateStrategy = new ApplyUpdateStrategy(bundleManager, eventBus, {
            applyDelayMs: 10_000,
          })
          const updater = new LiveUpdateService(
            appInfoProvider,
            bundleManager,
            versionFetcher,
            updateStrategy,
            {initialBundleId: initialBundleId, checkInterval: 60},
          )
          updater.runChecks()
        } catch (e) {
          console.error('[LiveUpdate bootstrap] Fatal error:', e)
          Sentry.captureException(new Error('[LiveUpdate bootstrap] Fatal error:', {cause: e}))
        }
      }

      waitReadyUpdates()
        .then(waitVersion)
        .then(version => checkUpdates(version))
    }),
  )
}

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart
